import { TripDriverWithDriverOutput } from '@allocab/allocab-driver-shared-types';
import {
  BoltSolid,
  BriefcaseSolid,
  CalendarDaysSolid,
  CarSolid,
  CircleDotSolid,
  ClockSolid,
  LocationDotSolid,
  RouteSolid,
} from '@allocab/allocab-icons';
import { getPanelNameLabelByKey } from '@allocab/allocab-shared-utils';

import { TripAssignButton } from './trip-assign-button/TripAssignButton';
import { TripPrice } from './TripPrice';
import { Link } from '../../components/atomic/Link';
import { BadgeIndicator } from '../../components/BadgeIndicator';
import { t } from '../../services/translation/translation';
import { getFullName } from '../../utils/common';
import { formatDateForParisTZ, humanizeDuration } from '../../utils/date';
import { getTripStatus, shortenFormattedAddress } from '../../utils/trip';

type Props = {
  trip: TripDriverWithDriverOutput;
  isAssignable: boolean;
};

export const TripCard = ({ trip, isAssignable }: Props) => {
  return (
    <div className="max-w-full p-3 bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700 space-y-1">
      <div className="text-xs flex flex-row justify-between items-center gap-1">
        <div className="flex gap-1 items-center">
          {trip.tripVars.flash ? (
            <BoltSolid width={12} height={12} className="fill-yellow-600" />
          ) : (
            <CalendarDaysSolid width={12} height={12} className="fill-gray-500" />
          )}
          <span className="whitespace-nowrap font-bold">
            {formatDateForParisTZ(trip.resume.departureAt, t('date.datetime'))}
          </span>
        </div>
        <BadgeIndicator variant={trip.state === 'DONE' ? 'green' : trip.state === 'CANCELLED' ? 'red' : 'yellow'}>
          {getTripStatus(trip)}
        </BadgeIndicator>
      </div>

      <div className="flex gap-1">
        <div className="p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600 flex-1">
          <span>{getPanelNameLabelByKey(trip.panelVars.key)}</span>
        </div>
        <div className="p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600 flex items-center gap-1">
          <BriefcaseSolid width={12} height={12} className="fill-current" />
          <span>{trip.account.name}</span>
        </div>
        <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-600">
          <TripPrice trip={trip} />
        </div>
      </div>

      <div className="flex items-stretch  gap-1">
        <div className="p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600 flex-1">
          <div className="flex items-center gap-2">
            <CircleDotSolid width={12} height={12} className="fill-orange-400" />
            <p className="flex-1 min-w-0 text-ellipsis overflow-hidden">
              {shortenFormattedAddress(trip.resume.start.formattedAddress)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <LocationDotSolid width={14} height={14} className="fill-current" />
            <p className="flex-1 min-w-0">{shortenFormattedAddress(trip.resume.end.formattedAddress)}</p>
          </div>
        </div>

        <div className="p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600 flex flex-col justify-around">
          <div className="flex items-center gap-1">
            <RouteSolid width={10} height={10} className="fill-current" />
            <p className="text-xs">{`~${Math.round(trip.resume.distance / 1000)} km`}</p>
          </div>
          <div className="flex items-center gap-1">
            <ClockSolid width={10} height={10} className="fill-current" />
            <p className="text-xs">{`~${humanizeDuration(trip.resume.duration)}`}</p>
          </div>
        </div>
      </div>

      <div className="flex items-stretch gap-1">
        <div className="flex items-center gap-2 flex-1 p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600">
          <CarSolid className="fill-current" width={13} height={13} />
          <Link to={`/drivers/${trip.driverKey}`}>{getFullName(trip.driverFirstName, trip.driverLastName)}</Link>
        </div>
        {isAssignable && trip.state === 'INCOMING' ? <TripAssignButton tripKey={trip.key} /> : null}
      </div>
    </div>
  );
};
